import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Trash, WarningCircle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { devApi } from './devApi';

// バックエンド（eng_development.DELETE_CONFIRM_WORD）と同じ固定文字。
// 案件名ではなくこの語を打たせる — 長い案件名の写し間違いで諦めるより、
// 「削除」と打つ一手間のほうが意思確認として確実。
export const DELETE_CONFIRM_WORD = '削除';

/**
 * 開発案件の削除確認。ソフトデリートなので行は消えず、履歴・由来リンクは残る。
 * 確認語を打つまで実行ボタンは押せない（バックエンドでも同じ語を検証する）。
 */
export const DevDeleteDialog = ({ open, onOpenChange, dev, onDeleted }) => {
  const [word, setWord] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setWord(''); setReason(''); } }, [open]);

  const ok = word.trim() === DELETE_CONFIRM_WORD;

  const run = async () => {
    setBusy(true);
    try {
      await devApi.remove(dev.id, { confirm: word.trim(), reason: reason.trim() || undefined });
      toast.success('開発案件を削除しました（復元できます）');
      onOpenChange(false);
      onDeleted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || '削除できませんでした');
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white" data-testid="dev-delete-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base text-rose-700">
            <WarningCircle size={18} /> 開発案件を削除する
          </DialogTitle>
          <DialogDescription className="text-sm">
            「{dev?.title}」を一覧・ダッシュボード・検索から外します。
            記録そのものは残るため、過去のレポートと、この案件から登録した技術資産の
            由来は壊れません。管理者はあとから復元できます。
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold text-zinc-700">
              確認のため「{DELETE_CONFIRM_WORD}」と入力してください
            </Label>
            <Input value={word} onChange={(e) => setWord(e.target.value)}
              placeholder={DELETE_CONFIRM_WORD} data-testid="dev-delete-confirm-input" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-zinc-700">理由（任意・履歴に残ります）</Label>
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="例: 案件が中止になったため" data-testid="dev-delete-reason-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
            data-testid="dev-delete-cancel-btn">やめる</Button>
          <Button onClick={run} disabled={!ok || busy}
            className="bg-rose-600 text-white hover:bg-rose-700"
            data-testid="dev-delete-submit-btn">
            <Trash size={15} className="mr-1" /> {busy ? '削除中…' : '削除する'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
